// AI-CONTEXT: See docs/features/afspraak-soort-en-aanwezigheid-bkb.md
// Hoe een roosterregel in de agenda verschijnt: als blok (zoals altijd), als
// achtergrond achter de afspraken, of niet. Verzoek BuurtKlusBedrijf: het
// rooster als extra blok leidt af van de echte afspraken.
//
// Nodig omdat resolveAgendaOptions alleen de opgeslagen instelling leest; de
// uitkomst hangt óók af van de weergave en het medewerkerfilter. Eén plek, zodat
// de agenda en het inplanvenster niet uiteenlopen.

import { scheduleCutoff } from './agendaSchedulePersonas'

/** @typedef {'block'|'background'|'hidden'} ScheduleDisplay */

/**
 * @param {object} args
 * @param {boolean} [args.fixtool] - `householdPlanningEnabled(settings)`; strikt, nog niet geladen = uit
 * @param {*} [args.setting] - `resolveAgendaOptions(...).scheduleDisplay`
 * @param {string|null} [args.viewType] - FullCalendar's `view.type`
 * @param {'personas'|'adresses'} [args.timelineMode] - Wat er in het rooster links onder elkaar staat
 * @param {Array<string>} [args.selectedPersonaIds] - Het medewerkerfilter; leeg = iedereen
 * @returns {ScheduleDisplay}
 */
const scheduleDisplayFor = ({ fixtool, setting, viewType, timelineMode, selectedPersonaIds }) => {
    // Alleen FIXtool: bij een dagbesteding is het rooster de aanwezigheidsregistratie
    // zelf, niet de achtergrond van een planning.
    if (fixtool !== true || setting !== 'background') return 'block'
    if (typeof viewType !== 'string') return 'block'

    // Een rij is één medewerker; een huishoudenrij heeft geen rooster.
    if (viewType.startsWith('resourceTimeline')) {
        return timelineMode === 'personas' ? 'background' : 'hidden'
    }

    // Een dagkolom kan niet per medewerker wit of grijs zijn: alleen met precies
    // één medewerker in het filter (ook 'Mijn afspraken') hoort de kolom bij één persoon.
    if (viewType.startsWith('timeGrid')) {
        return Array.isArray(selectedPersonaIds) && selectedPersonaIds.length === 1 ? 'background' : 'hidden'
    }

    // Een lijst kent geen achtergrond; de maand tekent een getimede achtergrond niet.
    if (viewType.startsWith('list')) return 'block'
    if (viewType.startsWith('dayGrid')) return 'hidden'

    return 'block'
}

/**
 * Wie een roosterregel in beeld heeft. Alleen díe baan wordt grijs buiten het
 * rooster: zonder rooster weten we niet of iemand er is, en is hij niet 'de hele
 * week afwezig'. Een los blok na de uitstroomdatum telt niet mee.
 *
 * @param {Array<object>} docs - De taken in beeld
 * @param {Record<string, Date>} endById - Laatste roosterdag per uitgestroomde deelnemer
 * @returns {Set<string>}
 */
const personaIdsWithSchedule = (docs, endById) => new Set((docs || [])
    .filter((d) => d?.Type === 'personaSchedule' && d.PersonaID && !scheduleCutoff(d, endById).past)
    .map((d) => d.PersonaID))

/**
 * De grijze klassen buiten het rooster (calendar.css): per baan in het rooster,
 * en voor de hele weergave in week en dag met één gefilterde medewerker.
 *
 * @param {object} args
 * @param {ScheduleDisplay} args.display - Uit scheduleDisplayFor
 * @param {string|null} [args.viewType] - FullCalendar's `view.type`
 * @param {Set<string>} args.idsWithSchedule - Uit personaIdsWithSchedule
 * @param {Array<string>} [args.selectedPersonaIds] - Het medewerkerfilter
 * @returns {{laneClassNames: (resourceId: string|undefined) => Array<string>, viewClassNames: Array<string>}}
 */
const scheduleOffClasses = ({ display, viewType, idsWithSchedule, selectedPersonaIds }) => {
    const background = display === 'background'
    const singleId = Array.isArray(selectedPersonaIds) && selectedPersonaIds.length === 1 ? selectedPersonaIds[0] : null
    const offView = background
        && typeof viewType === 'string' && viewType.startsWith('timeGrid')
        && idsWithSchedule.has(singleId)
    return {
        laneClassNames: (resourceId) => (background && idsWithSchedule.has(resourceId) ? ['fc-schedule-off'] : []),
        viewClassNames: offView ? ['fc-schedule-off-view'] : [],
    }
}

/**
 * Een roosterevent als vlak. Zonder eigen kleur: die zou het wit uit
 * calendar.css overschrijven.
 *
 * @param {object} event - Een FullCalendar EventInput
 * @returns {object}
 */
const asScheduleBackground = ({ color, backgroundColor, borderColor, textColor, ...event }) => ({
    ...event,
    display: 'background',
    classNames: ['fc-schedule-bg'],
})

export { personaIdsWithSchedule, scheduleOffClasses, asScheduleBackground }
export default scheduleDisplayFor
